/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import ReactFlow, { addEdge, Background, ControlButton, Controls, MiniMap, ReactFlowProvider, updateEdge, useEdgesState, useNodesState, useReactFlow, useStore } from 'reactflow';
import dagre from 'dagre';

// Component imports
import { Button, Col, Dropdown, Grid, Modal, Popover, Row, Space, Spin, Tooltip, Typography } from 'antd';
import { BorderOuterOutlined, DownloadOutlined, FullscreenExitOutlined, FullscreenOutlined, InfoCircleOutlined, PartitionOutlined, PlusCircleOutlined, SettingOutlined } from '@ant-design/icons';
import Prompt from '../UI/Prompt';
import AddNodePanel from './AddNodePanel';
import NodeEditForm from './NodeEditForm';
import GraphExportModal from '../Modals/GraphExportModal';
import SideModal from '../Layout/SideModal';
import { StickyNoteIcon } from '../Icons/StickyNoteIcon';
import { UndoIcon } from '../Icons/UndoIcon';
import { RedoIcon } from '../Icons/RedoIcon';
import { CodeIcon } from '../Icons/CodeIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { sanitizeFlowGraph, validateFlowGraphBeforeSubmit, validateFlowGraphEdges } from '../../utils/transform/flowGraph';
import { toggleFullScreenMode } from '../../store/global/globalActions';
import { ANALYTICS_VIEW_MODE_ACTIVE_NODES, DEFAULT_GRAPH_LAYOUT, DEFAULT_NODE_HEIGHT, DEFAULT_NODE_WIDTH, FLOWGRAPH_CLIPBOARD_KEY, FLOWGRAPH_SANITIZATION_SCHEMA_TYPES, GRAPH_LAYOUTS, NODE_SANITIZATION_SCHEMA, NODE_TYPES, NODE_TYPES_COMPONENTS, NODE_TYPES_PROPERTIES, SWITCH_CASE_CONDITIONS } from '../../constants/flowGraph';
import { useStateWithHistory } from '../../utils/hooks/useStateHistory';
import { PERMISSIONS_GROUP_EDITOR } from '../../constants/main';
import { generateRandomString } from '../../utils/string/generateRandomString';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { updateStoryVariablesWithLocalNodes } from '../../store/story/storySlice';
import is from '../../utils/is';
import useBeforeUnload from '../../utils/hooks/useBeforeUnload';
import extend from '../../utils/object/extend';
import useUserPermissions from '../../utils/hooks/useUserPermissions';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';
import generateIdFromContext from '../../utils/string/generateIdFromContext';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;
const baseClassName = 'conversation-graph';
export const GraphContext = React.createContext({});

//#region External utils

/**
 * Strips a list of graph elements of unneeded values (eg. if they are selected or being dragged)
 * @param {Array} elements
 * @returns A sanitized list of the given elements
 */
const stripElements = (elements) => {
  return (elements).map(el => {
    const { selected, dragging, width, height, positionAbsolute, targetPosition, sourcePosition, ...rest } = el;
    return rest;
  });
};

/**
 * Compares two lists of elements
 * @param {Array} oldElements
 * @param {Array} newElements
 * @returns {Boolean} Whether the two lists are different
 */
const areElementsChanged = (oldElements, newElements) => {
  return JSON.stringify(stripElements(oldElements.sort((a, b) => a.id.localeCompare(b.id))))
   !== JSON.stringify(stripElements(newElements.sort((a, b) => a.id.localeCompare(b.id))));
};

/**
 * Sanitizes a localized object by filtering out unsupported languages
 * and ensuring a primary language value exists.
 * @param {Object} obj The localized object to sanitize
 * @param {*} fallbackValue The default value if primary language is missing
 * @param {Array} supportedBotLanguages List of supported language codes
 * @param {String} primaryBotLanguage The primary language code
 */
const sanitizeLocales = (obj, fallbackValue, supportedBotLanguages, primaryBotLanguage) => {
  if (!obj || typeof obj !== 'object') {
    return;
  }

  Object.keys(obj).forEach(key => {
    if (!supportedBotLanguages.includes(key)) {
      delete obj[key];
    }
  });

  // Handle missing primary language requirement
  if (fallbackValue !== undefined && !obj[primaryBotLanguage]) {
    obj[primaryBotLanguage] = fallbackValue;
  }
};

/**
 * Generic processor to sanitize a node based on the Schema
 */
const sanitizeNodeData = (node, botLanguages, primaryBotLanguage) => {
  const data = node.data;
  if (!data) {
    return;
  }

  // 1. Sanitize First level properties
  NODE_SANITIZATION_SCHEMA._firstLevelProperties.forEach(field => {
    if (data[field]) {
      sanitizeLocales(data[field], '', botLanguages, primaryBotLanguage);
    }
  });

  // 2. Look up Node-Specific Rules
  const sanitizationRule = NODE_SANITIZATION_SCHEMA[node.type] || [];

  const targetPropertyToSanitize = data[sanitizationRule.path];
  if (!targetPropertyToSanitize) {
    return;
  }

  switch(sanitizationRule.type) {
    case FLOWGRAPH_SANITIZATION_SCHEMA_TYPES.DEFAULT:
      // Simple Object (e.g., MultiOptions)
      sanitizeLocales(targetPropertyToSanitize, sanitizationRule.fallbackValue || {}, botLanguages, primaryBotLanguage);
      break;
    case FLOWGRAPH_SANITIZATION_SCHEMA_TYPES.ARRAY:
      // Array of Objects (e.g., CSAT Sections, User Info)
      targetPropertyToSanitize.forEach(item => {
        sanitizationRule.fields.forEach(subField => {
          sanitizeLocales(item[subField], '', botLanguages, primaryBotLanguage);
        });
      });
      break;
    case FLOWGRAPH_SANITIZATION_SCHEMA_TYPES.LANGUAGE_MAP:
      // Language Map (e.g., Carousel Cards where keys are 'en', 'es', etc.)
      targetPropertyToSanitize.forEach(item => {
        Object.keys(item).forEach(key => {
          if (!botLanguages.includes(key)) {
            delete item[key];
          }
        });
      });
      break;
    default:
      return;
  }
};

//#endregion

const WrappedFlowGraph = props => {
  const {
    graphId = 'flow-graph',
    isLoading = false,
    isViewOnlyMode = false,
    injectedNodeData = {},
    disabledNodeTypes = [],
    onUpdate = () => {},
    initialGraphDirection = GRAPH_LAYOUTS.VERTICAL,
    initialGraphElements = [],
    allowLayoutChange = true,
    graphSettingsForm = null,
    hasPendingChanges = false,
    setHasPendingChanges = () => {}
  } = props;
  const reactFlowWrapper = useRef(null);
  const nodeEditFormRef = useRef(null);
  const dispatch = useDispatch();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = screens.xs;
  const userPermissions = useUserPermissions(orgId, tenantId);
  const { primaryBotLanguage, currentBotLanguage, isEditingPrimaryLanguage, botLanguages } = useBotLanguage();
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission)) && !isViewOnlyMode;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mousePositionRef = useRef({ x: 0, y: 0 });

  // Refs
  const addNewNodePanelRef = useRef(null);

  // React flow store
  const unselectNodesAndEdges = useStore((state) => state.unselectNodesAndEdges);
  const addSelectedNodes = useStore((state) => state.addSelectedNodes);
  const addSelectedEdges = useStore((state) => state.addSelectedEdges);
  const { getNodes, getEdges, setCenter } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [elementsForHistory, setElementsForHistory, elementsHistory] = useStateWithHistory({ nodes: [], edges: [], settings: {} }, 100);
  const [nodeToEdit, setNodeToEdit] = useState(null);
  const [nodeToEditMetadata, setNodeToEditMetadata] = useState(null);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_GRAPH_LAYOUT);
  const [isAddNodePanelOpen, setIsAddNodePanelOpen] = useState(false);

  // Track the latest saved state for comparing pending changes
  const [latestSavedElements, setLatestSavedElements] = useState([]);

  const { isFullScreenMode } = useSelector((state) => state.global);

  // Util to get a list of all nodes and edges in the graph
  const getCurrentElements = () => {
    if (reactFlowInstance) {
      return [...nodes, ...edges];
    } else {
      return [];
    }
  };

  // Prevent page refresh and direct navigation if there are pending changes
  // Note: Router based navigation is covered by the <Prompt /> component below
  useBeforeUnload(hasPendingChanges && !isViewOnlyMode, t('Are you sure you want to leave the page? Any unsaved changes will be lost.'));

  /**
   * Save changes to history
   * @param {Array} newElements The new history state
   */
  const updateHistory = (newElements, newLayout = layout) => {
    if (!reactFlowInstance || newElements?.length === 0 || elementsForHistory?.nodes?.length === 0) {
      return;
    }

    if (areElementsChanged([...elementsForHistory.nodes, ...elementsForHistory.edges], newElements)) {
      const strippedElements = stripElements(newElements);
      setElementsForHistory({
        layout: newLayout,
        nodes: strippedElements.filter(el => el.data.nodeType === 'node'),
        edges: strippedElements.filter(el => el.data.nodeType === 'edge')
      });
    }
  };

  /**
   * Adds newly created elements to the graph state and handles side effects.
   * This is used by both clone and paste.
   */
  const addElementsToGraph = (newElements, shouldUpdateVariables) => {
    const elementsToSet = [...getCurrentElements(), ...newElements];
    setNodes(elementsToSet.filter(el => el.data.nodeType === 'node'));
    setEdges(elementsToSet.filter(el => el.data.nodeType === 'edge'));

    setTimeout(() => {
      unselectNodesAndEdges();
      addSelectedEdges(newElements.filter(el => el.data.nodeType === 'edge').map(e => e.id));
      addSelectedNodes(newElements.filter(el => el.data.nodeType === 'node').map(n => n.id));
    }, 0);

    updateHistory(elementsToSet);
    if (shouldUpdateVariables) {
      dispatch(updateStoryVariablesWithLocalNodes(orgId, tenantId, newElements.filter(el => el.data.nodeType === 'node'), searchParams.get('activity')));
    }
  };

  const pasteElements = useCallback((elementsToPaste, pastePosition) => {
    if (!elementsToPaste || elementsToPaste.length === 0) {
      return;
    }
    const copiedNodes = elementsToPaste.filter(el => el.data.nodeType === 'node');
    if (copiedNodes.length === 0) {
      return;
    }

    // Calculate the position based on the top left selected node
    const topLeftNodePos = copiedNodes.reduce((acc, node) => ({
      x: Math.min(acc.x, node.position.x),
      y: Math.min(acc.y, node.position.y)
    }), { x: Infinity, y: Infinity });
    const offsetX = pastePosition.x - topLeftNodePos.x;
    const offsetY = pastePosition.y - topLeftNodePos.y;

    const idMapping = new Map();
    let newElements = structuredClone(elementsToPaste);
    let shouldUpdateVariables = false;

    newElements.forEach(element => {
      idMapping.set(element.id, uuidv4());
    });

    newElements.forEach(element => {
      element.id = idMapping.get(element.id);

      const isNodeWithQuestionId = [NODE_TYPES.SELECT, NODE_TYPES.FILE_UPLOAD, NODE_TYPES.INPUT_QUERY].includes(element.type);
      if (isNodeWithQuestionId) {
        shouldUpdateVariables = true;
      }

      if (element.data.nodeType === 'node') {

        element.position = { x: element.position.x + offsetX, y: element.position.y + offsetY };

        sanitizeNodeData(element, botLanguages, primaryBotLanguage);

        if (element.type === NODE_TYPES.CAROUSEL && element.data.cards) {
          element.data.cards = element.data.cards.map(card => {

            Object.keys(card).forEach(languageKey => {

              // Remap IDs for supported languages
              if (card[languageKey].buttonToEdgeMap) {
                const newCardId = uuidv4();
                card[languageKey] = {
                  ...card[languageKey],
                  id: newCardId,
                  buttonToEdgeMap: card[languageKey].buttonToEdgeMap.map(button => ({
                    ...button,
                    edgeId: idMapping.get(button.edgeId) || uuidv4()
                  }))
                };
              }
            });
            return card;
          });
        }
      } else if (element.data.nodeType === 'edge') {
        element.source = idMapping.get(element.source);
        element.target = idMapping.get(element.target);
      }
    });

    addElementsToGraph(newElements, shouldUpdateVariables);
  }, [dispatch, getCurrentElements, orgId, primaryBotLanguage, searchParams, setEdges, setNodes, tenantId, unselectNodesAndEdges, addSelectedNodes, addSelectedEdges, updateHistory]);

  //#region Effects

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if(isEditFormOpen) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isSave = (isMac ? event.metaKey : event.ctrlKey) && event.key.toLowerCase() === 's';

      if (isSave) {
        event.preventDefault();

        if (canEdit && hasPendingChanges && !isLoading) {
          onSaveElements();
        }
        return;
      }

      if(currentBotLanguage !== primaryBotLanguage) {
        return;
      }

      const isCopy = (isMac ? event.metaKey : event.ctrlKey) && event.key === 'c';
      const isPaste = (isMac ? event.metaKey : event.ctrlKey) && event.key === 'v';

      if (isCopy) {
        event.preventDefault();
        const selectedElements = [...getNodes(), ...getEdges()].filter(el => el.selected && el.type !== NODE_TYPES.INTRO);

        if (selectedElements.length > 0) {
          const clipboardData = {
            type: FLOWGRAPH_CLIPBOARD_KEY,
            payload: stripElements(selectedElements)
          };

          try {
            await navigator.clipboard.writeText(JSON.stringify(clipboardData));

            openConsoleAlertMessage({ type: 'success', message: 'Nodes copied to Clipboard!' });
          } catch (err) {
            console.error('Failed to copy elements to clipboard:', err);
          }
        }
      }

      if (isPaste) {
        event.preventDefault();
        try {
          const clipboardText = await navigator.clipboard.readText();
          const parsedData = JSON.parse(clipboardText);

          if (parsedData && parsedData.type === FLOWGRAPH_CLIPBOARD_KEY && Array.isArray(parsedData.payload)) {
            pasteElements(parsedData.payload, mousePositionRef.current);
          }
        } catch (err) {
          console.warn('Could not paste from clipboard:', err);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [getNodes, getEdges, pasteElements]);

  // When the conversation graph is fetched anew from the backend
  useEffect(() => {
    const transformedElements = [...initialGraphElements];
    if (isViewOnlyMode) {
      transformedElements.forEach((element) => {
        if (element.data.nodeType !== 'edge') {
          if (ANALYTICS_VIEW_MODE_ACTIVE_NODES.includes(element.type)) {
            if (injectedNodeData?.nodesIds?.includes(element.id)) {
              element.data = {
                ...element.data,
                appearances: injectedNodeData?.nodesData[element.id]?.appearances,
                isViewOnlyMode: isViewOnlyMode
              };
            } else {
              element.data = {
                ...element.data,
                appearances: 0,
                isViewOnlyMode: isViewOnlyMode
              };
            }
          }
        }
      });
    }

    const newDirection = initialGraphDirection || DEFAULT_GRAPH_LAYOUT;
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      transformedElements.filter(el => el.data.nodeType === 'node'),
      transformedElements.filter(el => el.data.nodeType === 'edge'),
      newDirection,
      false // We don't want to force a layout upon graph initialization
    );

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    setLayout(newDirection);

    dispatch(updateStoryVariablesWithLocalNodes(orgId, tenantId, layoutedNodes, searchParams.get('activity'))); // Update the variables list by appending any local variables to the fetched ones

    setLatestSavedElements(transformedElements);
  }, [graphId]);

  // The following function redraws the selected edge in the
  // DOM to make it possible for the user to update it.
  useEffect(() => {
    const selectedEdge = getEdges().find(e => e.selected);
    const selectedEdgeInDom = selectedEdge && document.querySelector('.react-flow__edge.selected');
    if (selectedEdgeInDom) {
      const selectedEdgeParent = selectedEdgeInDom.parentElement;

      if (selectedEdgeParent) {
        selectedEdgeParent.removeChild(selectedEdgeInDom);
        selectedEdgeParent.appendChild(selectedEdgeInDom);
      }
    }
  }, [edges]);

  // When the graph is first loaded, center it and initialize the history
  useEffect(() => {
    if (reactFlowInstance) {
      centerGraph(getCurrentElements().find(n => n.type === NODE_TYPES.INTRO), layout);

      // Initialize graph history
      elementsHistory.clear();
      setElementsForHistory({
        layout: layout,
        nodes: getCurrentElements().filter(el => el.data.nodeType === 'node'),
        edges: getCurrentElements().filter(el => el.data.nodeType === 'edge')
      });
    }
  }, [reactFlowInstance]);

  // When the graph is updated, recalculate pending changes
  useEffect(() => {
    const currentElements = getCurrentElements();

    // No need to check for pending changes if the user is dragging a node
    if (currentElements.some(el => el.dragging)) {
      return;
    }

    if (currentElements.length > 0 && areElementsChanged(currentElements, latestSavedElements)) {
      setHasPendingChanges(true);
    } else {
      setHasPendingChanges(false);
    }
  }, [nodes, edges]);

  // On undo or redo, update the graph
  useEffect(() => {
    if (elementsForHistory?.nodes?.length > 0 && areElementsChanged(getCurrentElements(), [...elementsForHistory.nodes, ...elementsForHistory.edges])) {
      setNodes(elementsForHistory.nodes.filter(el => el.data.nodeType === 'node'));
      setEdges(elementsForHistory.edges.filter(el => el.data.nodeType === 'edge'));
      setLayout(elementsForHistory.layout);
      unselectNodesAndEdges();
    }
  }, [elementsForHistory]);

  //#endregion

  //#region Utils

  /**
   * Pass through and sanitize any changes that happen to the nodes
   * Will call the onNodesChange handler
   * @param {Array} nodeChanges A list of changes happening to the nodes. Property 'type' indicates the type of the change.
   */
  const onNodesChanged = (nodeChanges) => {
    // Remove any delete attempts, as we are handling it through onElementsDelete
    nodeChanges = nodeChanges.filter(change => change.type !== 'remove');
    onNodesChange(nodeChanges);
  };

  /**
   * Pass through and sanitize any changes that happen to the edges
   * Will call the onEdgesChange handler
   * @param {Array} edgeChanges A list of changes happening to the edges. Property 'type' indicates the type of the change.
   */
  const onEdgesChanged = (edgeChanges) => {
    // Remove any delete attempts, as we are handling it through onElementsDelete
    edgeChanges = edgeChanges.filter(change => change.type !== 'remove');
    onEdgesChange(edgeChanges);
  };

  /**
   * Centers the graph in the middle of the screen - depending on the graph layout
   */
  const centerGraph = (introNode, graphLayout) => {
    if (introNode) {
      const x = introNode.position.x + DEFAULT_NODE_WIDTH / 2 + (graphLayout === GRAPH_LAYOUTS.VERTICAL ? 0 : 100);
      const y = introNode.position.y + DEFAULT_NODE_HEIGHT / 2 + (graphLayout === GRAPH_LAYOUTS.VERTICAL ? 250 : 0);
      setCenter(x, y, { zoom: 0.9 });
    }
  };

  /**
   * When an element is removed by pressing Delete
   * More than one element has been possibly removed.
   * That happens if a node is deleted, so its adjacent edges are removed as well.
   * That means we have to make sure all nodes are behaving properly with regards to their edges
   *
   * @param {Array} elementsToRemove The list of elements to remove (could include intro node too)
   */
  const onElementsRemove = (elementsToRemove) => {
    if (canEdit && isEditingPrimaryLanguage) {
      let newElements = getCurrentElements()
        .filter(el => !elementsToRemove
          .filter(element => element.type !== NODE_TYPES.INTRO) // Intro node should always stay inside the graph
          .some(elementToRemove => elementToRemove.id === el.id)
        );
      newElements = sanitizeFlowGraph(newElements);

      // Update the state of the graph
      setNodes(newElements.filter(el => el.data.nodeType === 'node'));
      setEdges(newElements.filter(el => el.data.nodeType === 'edge'));

      // Unselect the rest of the elements
      unselectNodesAndEdges();

      // Save changes to history
      updateHistory(newElements);
    }
  };

  /**
   * Util for deleting the currently selected nodes from the graph.
   * Will trigger the onElementsRemove function.
   */
  const deleteSelectedNodes = () => {
    const selectedElements = getNodes().filter(n => n.selected);
    if (is.array(selectedElements) && !is.emptyArray(selectedElements)) {
      onElementsRemove(selectedElements);
    }
  };

  /**
   * Duplicates selected elements and positions them near the original ones
   */
  const cloneSelectedElements = () => {
    let shouldUpdateVariables = false;
    if (canEdit && isEditingPrimaryLanguage) {
      const selectedElements = [...getNodes(), ...getEdges()].filter(el => el.selected);

      // Remove intro nodes from the selection
      const elementsToClone = selectedElements.filter(element => element.type !== NODE_TYPES.INTRO);

      let newElements = [...elementsToClone];
      newElements.forEach((element, index) => {
        const newId = uuidv4();
        const isNodeWithQuestionId = [NODE_TYPES.SELECT, NODE_TYPES.FILE_UPLOAD, NODE_TYPES.INPUT_QUERY].includes(element.type);
        if (isNodeWithQuestionId) {
          shouldUpdateVariables = true;
        }

        if (element.data.nodeType === 'node') {
          let updatedElement = {
            ...element,
            id: newId,
            position: {
              x: element.position.x + 180,
              y: element.position.y + 30
            },
            data: {
              ...element.data,
              ...(element.type === NODE_TYPES.LINK_OPTION || element.type === NODE_TYPES.OPTION) && { priority: null },
              ...(element.type === NODE_TYPES.CSAT) && { variableName: generateRandomString(6) },
              ...(element.type === NODE_TYPES.SEMANTIC_SEARCH && { variableName: generateIdFromContext('semsearch') }),
              ...(isNodeWithQuestionId) && { questionId: generateRandomString(6) }
            }
          };

          // Specific handling for carousel nodes
          if (element.type === NODE_TYPES.CAROUSEL) {
            updatedElement.data.cards = element.data.cards.map(card => {
              // Clone each card with new ID and updated buttonToEdgeMap
              const clonedCard = { ...card };

              // Iterate over each language key in card
              Object.keys(card).forEach(languageKey => {
                if (card[languageKey].buttonToEdgeMap) {
                  const newCardId = uuidv4();
                  clonedCard[languageKey] = {
                    ...card[languageKey],
                    id: newCardId, // New ID for the duplicated card

                    buttonToEdgeMap: card[languageKey].buttonToEdgeMap.map(button => {
                      const newEdgeId = uuidv4();
                      const oldEdge = newElements.find(el => el.id === button.edgeId);

                      if (oldEdge) {
                        // Clone the edge with a new ID
                        const clonedEdge = {
                          ...oldEdge,
                          id: newEdgeId,
                          source: newId, // Update source to the new carousel node
                          sourceHandle: newCardId // Set sourceHandle/sourcePositionId to the new card
                        };
                        newElements.push(clonedEdge);
                        return { ...button, edgeId: newEdgeId }; // Update button with new edge ID
                      }
                      return button;
                    })
                  };
                }
              });

              return clonedCard;
            });
          }

          newElements[index] = updatedElement;

          // Update edges referencing this node
          newElements.filter(el => el.target === element.id).forEach(edge => edge.target = newId);
          newElements.filter(el => el.source === element.id).forEach(edge => edge.source = newId);
        } else if (element.data.nodeType === 'edge') {
          newElements[index] = { ...element, id: uuidv4() };
        }
      });

      addElementsToGraph(newElements, shouldUpdateVariables);
    }
  };

  /**
   * When a new node is dropped (via drag & drop, not just a click) in the canvas
   * Calls addNewElements handler
   */
  const onNewNodeDrop = useCallback((event) => {
    event.preventDefault();

    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');

    if (!type) {
      return;
    }

    const position = reactFlowInstance.project({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });

    let newNode = {
      id: uuidv4(),
      type,
      position,
      data: {
        label: { [primaryBotLanguage]: '' },
        nodeType: 'node'
      }
    };

    addNewElements({ nodes: [newNode] });
    setTimeout(() => openEditForm(newNode.id));
  }, [reactFlowInstance]);

  /**
   * Validates and sanitizes current edges in the graph, called when a new edge is added in the graph
   * @param {Array} elementsToUpdate The elements we plan to update
   * @param {Object} newEdge The edge being added in the graph
   */
  const validateAndUpdateEdges = (elementsToUpdate, newEdge) => {
    validateFlowGraphEdges(getCurrentElements(), elementsToUpdate, newEdge).then(newElements => {
      setNodes(newElements.filter(el => el.data.nodeType === 'node'));
      setEdges(newElements.filter(el => el.data.nodeType === 'edge'));

      // Save changes to history
      updateHistory(newElements);
    }).catch((err) => {
      err && openConsoleAlertMessage({ type: 'warning', message: err, duration: 10 });
    });
  };

  /**
   * Checks whether a source is already connected to the selected target.
   * Connection for multiple source handles nodes should be allowed
   * @param {Object} newEdge The new edge parameters
   * @returns {boolean} True for valid, false for invalid connection
   */
  const checkForValidEdgeConnection = (newEdge) => {
    const edges = getEdges();
    const sourceNode = getNodes().find(node => node.id === newEdge.source);
    // Allow nodes with more than 1 source handles to connect to same target
    const isNodeWithMoreExits = [NODE_TYPES.SWITCH_QUERY, NODE_TYPES.INPUT_QUERY, NODE_TYPES.FILE_UPLOAD, NODE_TYPES.CAROUSEL, NODE_TYPES.AUTHENTICATE_USER, NODE_TYPES.IS_AUTHENTICATED].includes(sourceNode.type);
    const edgeExists = !isNodeWithMoreExits && edges.find((edge) => edge.source === newEdge.source && edge.target === newEdge.target);
    if (edgeExists) {
      openConsoleAlertMessage({ type: 'warning', message: t('An edge connecting these nodes already exists.') });
      return false;
    }
    return true;
  };

  /**
   * When a new edge is created & connected to a node
   * @param {Object} edgeParams The edge parameters
   */
  const onEdgeConnect = (edgeParams) => {
    const newEdge = {
      ...edgeParams,
      type: 'default',
      arrowHeadType: 'arrowclosed',
      data: {
        label: null,
        nodeType: 'edge'
      }
    };

    if (checkForValidEdgeConnection(newEdge)) {
      let newElements = addEdge(newEdge, getCurrentElements());
      validateAndUpdateEdges(newElements, newEdge);
    }
  };

  /**
   * Whenever an existing edge is updated (changes source or target node)
   * @param {Object} oldEdge The old edge
   * @param {Object} newEdge The new edge
   */
  const onEdgeUpdate = (oldEdge, newEdge) => {
    if (oldEdge.source === newEdge.source) {
      // If the user doesn't connect the edge to a new node, do nothing
      if (oldEdge.target === newEdge.target) {
        return;
      } else {
        // If an edge already exists between the nodes, do nothing, unless the node has more than 1 exits (sourceHandles)
        if (!checkForValidEdgeConnection(newEdge)) {
          return;
        }
      }
    }

    let updatedElements = updateEdge(oldEdge, newEdge, getCurrentElements());
    validateAndUpdateEdges(updatedElements, newEdge);
  };

  /**
   * When the user clicks anywhere outside the nodes & edges
   */
  const onPaneClick = () => {
    unselectNodesAndEdges();
  };

  /**
   * When any pending changes are committed to the backend
   */
  const onSaveElements = async (locationToNavigate = null) => {
    // First remove all front end errors as we are going to validate again
    Array.from(document.getElementsByClassName('has-frontend-error')).forEach(node=> node.classList.remove('has-frontend-error'));

    try {
      const currentElements = getCurrentElements();

      // First, do a local validation
      await validateFlowGraphBeforeSubmit(currentElements, primaryBotLanguage, currentBotLanguage);

      // Submit the changes if validation passes
      const saveResult = await onUpdate(currentElements, { locationToNavigate });

      if (!saveResult.error) {
        setLatestSavedElements(currentElements);
        setHasPendingChanges(false);
        return true;
      } else {
        setHasPendingChanges(true);
        return false;
      }
    } catch (error) {
      // Handle validation or update errors
      if (error.message) {
        if (error.metadata && error.metadata.nodeId) {
          // Apply front end errors to the respective nodes
          document.querySelector(`[data-id="${error.metadata.nodeId}"]`)?.firstElementChild.classList?.add('has-frontend-error');
        }
        openConsoleAlertMessage({ type: 'error', message: error.message });
      }

      setHasPendingChanges(true);
      return false;
    }
  };

  /**
   * Callback for whenever a graph element has been updated
   * @param {Array} updatedElements The elements that have been updated
   */
  const onElementsUpdated = (updatedElements) => {
    const currentElements = getCurrentElements();
    if (!currentElements || is.emptyArray(currentElements)) {
      return;
    }

    let elementsToSet = [...currentElements];

    updatedElements.forEach(el => {
      const [elementId, updatedValues] = Object.entries(el)[0];
      const updatedIndex = elementsToSet.findIndex((element => element.id === elementId));

      // Override updated element with new values
      elementsToSet[updatedIndex] = {
        ...elementsToSet[updatedIndex],
        ...updatedValues,
        data: {
          ...elementsToSet[updatedIndex].data,
          ...updatedValues.data
        }, selected: true
      };

      // In case the ID of the node was also updated
      // we need to also update the edge connected to it (as they have a reference to that ID)
      if (updatedValues.id && elementId !== updatedValues.id) {
        const incomingEdges = elementsToSet.filter(element => element.target === elementId);
        const outgoingEdges = elementsToSet.filter(element => element.source === elementId);

        outgoingEdges.forEach(edge => {
          const index = elementsToSet.findIndex((element => element.id === edge.id));
          elementsToSet[index] = extend(elementsToSet[index], { source: updatedValues.id });
        });

        incomingEdges.forEach(edge => {
          const index = elementsToSet.findIndex((element => element.id === edge.id));
          elementsToSet[index] = extend(elementsToSet[index], { target: updatedValues.id });
        });
      }

      // If the question ID (data.questionId) has been updated,
      // also update its references on flow control nodes (if any)
      if (updatedValues.data?.questionId && updatedValues.data.questionId !== currentElements[updatedIndex].data?.questionId) {
        const referencedNodes = elementsToSet.filter(element => element.type === NODE_TYPES.SWITCH_VARIABLE && element.data?.switchVariable === `survey.${currentElements[updatedIndex].data?.questionId}.answer`);

        referencedNodes.forEach(node => {
          const index = elementsToSet.findIndex((element => element.id === node.id));
          elementsToSet[index] = extend(elementsToSet[index], { data: { switchVariable: `survey.${updatedValues.data.questionId}.answer` } });
        });
      }
    });

    const tempNodes = elementsToSet.filter(el => el.data.nodeType === 'node');
    setNodes(tempNodes);
    setEdges(elementsToSet.filter(el => el.data.nodeType === 'edge'));

    // Save changes to history
    updateHistory(elementsToSet);
    // Update the variables list by appending any local variables to the fetched ones
    dispatch(updateStoryVariablesWithLocalNodes(orgId, tenantId, tempNodes, searchParams.get('activity')));
  };

  /**
   * Handler for whenever an element is added in the graph
   * @param {Array} newElements The new elements to add
   */
  const addNewElements = (newElements) => {
    const { nodes, edges } = newElements;
    let newNodes = [];
    let newEdges = edges ? [...edges] : [];
    let shouldUpdateVariables = false;

    // Handle nodes
    nodes.forEach(node => {
      let newNode = { ...node };

      // Pre-select new node
      newNode.selected = true;

      if (!newNode.position) {
        // Position the new node to the same place (taking into account zooming & panning)
        newNode.position = reactFlowInstance.project({ x: 280, y: 100 });
      }

      switch (newNode.type) {
        case NODE_TYPES.MESSAGE:
          newNode.data.enableBackAction = false;
          break;
        case NODE_TYPES.SELECT:
          newNode.data.enableBackAction = true;
          newNode.data.isDropdown = false;
          break;
        case NODE_TYPES.OPTION:
        case NODE_TYPES.LINK_OPTION:
          newNode.data.priority = newNode.data?.priority || 1;
          break;
        case NODE_TYPES.MULTI_OPTION:
          newNode.data.multiOptions = { [primaryBotLanguage]: { '': '' } };
          break;
        case NODE_TYPES.SWITCH_CASE:
          newNode.data.switchCaseData = {
            condition: SWITCH_CASE_CONDITIONS.EQUAL
          };
          break;
        case NODE_TYPES.SWITCH_VARIABLE:
          newNode.data.controlNodeType = NODE_TYPES.SELECT;
          break;
        case NODE_TYPES.HTTP_REQUEST:
          newNode.data.httpRequestData = {
            headers: { },
            url: '',
            mappings: [],
            method: 'GET'
          };
          break;
        case NODE_TYPES.SEND_STORED_DATA:
          newNode.data.sendStoredData = {
            headers: { '': '' },
            callbackId: generateRandomString(6)
          };
          break;
        case NODE_TYPES.TEXT_INPUT:
          newNode.data.inputIsMandatory = true;
          break;
        case NODE_TYPES.FILE_UPLOAD:
          newNode.data.label = { [primaryBotLanguage]: t('Please use the clip icon to upload a file.') };
          newNode.data.questionId = generateRandomString(6);
          shouldUpdateVariables = true;
          break;
        case NODE_TYPES.USER_FEEDBACK:
          newNode.data.jumpProbability = 1;
          break;
        case NODE_TYPES.ACTION:
          newNode.data.actionData = {
            parameters: [{ type: '', value: '' }]
          };
          break;
        case NODE_TYPES.SEMANTIC_SEARCH:
          newNode.data.variableName = generateIdFromContext('semsearch');
          break;
        default:
          break;
      }

      newNodes.push(newNode);
    });

    const newElementState = sanitizeFlowGraph(getCurrentElements().concat([...newNodes, ...newEdges]));

    setNodes(newElementState.filter(el => el.data.nodeType === 'node'));
    setEdges(newElementState.filter(el => el.data.nodeType === 'edge'));

    // Unselect the rest of the elements
    unselectNodesAndEdges();

    // Save changes to history
    updateHistory(newElementState);
    if (shouldUpdateVariables) {
      // Update the variables list by appending the local variable added for the file upload node
      dispatch(updateStoryVariablesWithLocalNodes(orgId, tenantId, newNodes, searchParams.get('activity')));
    }
  };

  /**
   * Opens the edit form modal and sets the chosen node as the one to edit
   * @param {String} nodeIdToEdit (Optional) The ID of the node we want to edit
   * @param {Object} metadata (Optional) Any extra properties to pass to the edit form
   */
  const openEditForm = (nodeIdToEdit, metadata) => {
    if (nodeIdToEdit) {
      const nextNodeToEdit = getNodes().find(node => node.id === nodeIdToEdit);
      if (nextNodeToEdit && NODE_TYPES_PROPERTIES[nextNodeToEdit.type]?.hasEditForm === false) {
        return;
      }
      setNodeToEdit(nextNodeToEdit);
    }

    if (metadata) {
      setNodeToEditMetadata(metadata);
    }

    if (!isEditFormOpen) {
      setIsEditFormOpen(true);
    }
  };

  /**
   * Closes the edit form modal
   */
  const closeEditForm = () => {
    setNodeToEdit(null);
    setIsEditFormOpen(false);
    setNodeToEditMetadata(null);
  };

  /**
   * Open the add new node panel
   */
  const openAddNewNodePanel = () => {
    setIsAddNodePanelOpen(true);
  };

  /**
   * Close the add new node panel
   */
  const closeAddNewNodePanel = () => {
    setIsAddNodePanelOpen(false);
    addNewNodePanelRef?.current?.clearSearchInput?.();
  };

  /**
  * Gets layouted elements for a graph
  * https://github.com/dagrejs/dagre/wiki#using-dagre
  * @param {Object[]} nodes The nodes to layout
  * @param {Object[]} edges The edges to layout
  * @param {String} direction The direction of the graph
  * @param {Boolean} snapToLayout Whether to snap the nodes to the layout - otherwise they will be positioned at the same coordinates as the original nodes
  * @returns {Object} An object containing the layouted nodes and edges
  */
  const getLayoutedElements = (nodes, edges, direction = DEFAULT_GRAPH_LAYOUT, snapToLayout = true) => {
    // Util to round to nearest 10 for snapping the nodes to the grid
    const roundToNearest10 = (number) => Math.floor(number / 10) * 10;

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: direction === GRAPH_LAYOUTS.HORIZONTAL ? 'LR' : 'TB' });

    nodes.forEach(({ id, width = DEFAULT_NODE_WIDTH, height = DEFAULT_NODE_HEIGHT }) => {
      dagreGraph.setNode(id, { width, height });
    });

    edges.forEach(({ source, target }) => {
      dagreGraph.setEdge(source, target);
    });

    dagre.layout(dagreGraph);

    return {
      nodes: nodes.map((node) => {
        const isHorizontal = direction === GRAPH_LAYOUTS.HORIZONTAL;
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
          ...node,
          targetPosition: isHorizontal ? 'left' : 'top',
          sourcePosition: isHorizontal ? 'right' : 'bottom',
          position: {
            x: snapToLayout ? roundToNearest10(nodeWithPosition.x - (node.width || DEFAULT_NODE_WIDTH) / 2) : node.position.x,
            y: snapToLayout ? roundToNearest10(nodeWithPosition.y - (node.height || DEFAULT_NODE_HEIGHT) / 2) : node.position.y
          }
        };
      }),
      edges
    };
  };

  /**
  * Change the layout of the graph
  * @param {String} newDirection The desired direction of the graph
  */
  const changeLayoutDirection = (newDirection) => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges, newDirection);
    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
    setLayout(newDirection);

    centerGraph(layoutedNodes.find(n => n.type === NODE_TYPES.INTRO), newDirection);
    updateHistory([...layoutedNodes, ...layoutedEdges], newDirection);
  };

  /**
   * Renders the title of the edit form modal
   * @param {Object} nodeProps The props of the node to edit
   * @returns {JSX.Element} The title of the edit form modal
   */
  const renderNodeTitle = nodeProps => {
    const modalTypeTitle = NODE_TYPES_PROPERTIES[nodeProps.type].label.toLowerCase().replace('http', 'HTTP').replace('llm', 'LLM');
    let modalTitle = `${t('Edit')} ${modalTypeTitle} ${t('node')}`;

    if (nodeProps.type === NODE_TYPES.STICKY_NOTE) {
      modalTitle = nodeProps.label;
    }

    return (
      <Text>
        {modalTitle}
        <Tooltip
          placement="right"
          title={nodeProps.info}
        >
          <InfoCircleOutlined className="ml-3"/>
        </Tooltip>
      </Text>
    );
  };

  const onDoubleClick = (event) => {
    const selectedElements = getCurrentElements().filter(el => el.selected);

    if (selectedElements.length > 0) {
      // If the user double clicked on the same node, open the edit form - avoid opening the edit form when double clicking on the canvas
      const parentId = selectedElements[0].id;
      const clickedElement = event.target;
      const parentWithSameDataId = clickedElement.closest(`[data-id="${parentId}"]`); // Check if the clicked element is the same as the selected node
      const parentWithNodeTooltipClass = clickedElement.closest('.node-tooltip'); // Check if the clicked element is the node tooltip as we don't want to open the edit form when double clicking on the tooltip

      if (parentWithSameDataId && !parentWithNodeTooltipClass) {
        openEditForm(parentId);
      }
    }
  };

  //#endregion

  const handleAddPanelCloseOnClick = (event) => {
    if (isAddNodePanelOpen) {
      const addPanelElement = addNewNodePanelRef?.current.getPanelElement();
      if (addPanelElement && addPanelElement.contains(event.target)) {
        return; // Click is inside the panel, don't close it
      }
      const isDropdownButton = event.target.closest('.ant-dropdown-trigger');
      if (!isDropdownButton) {
        setIsAddNodePanelOpen(false);
      }
    }
  };

  // Handle click on sticky note icon to add a sticky note "node"
  const handleStickyNoteIconClick = (event) => {
    let newNode = {
      id: uuidv4(),
      type: NODE_TYPES.STICKY_NOTE,
      data: {
        label: { [primaryBotLanguage]: '' },
        nodeType: 'node'
      }
    };
    addNewElements({ nodes: [newNode] });
    setTimeout(() => {
      openEditForm(newNode.id);
    }, 0);
  };

  if (!nodes || is.emptyArray(nodes)) {
    return (
      <Row className="w-100">
        <Col flex="auto" className="text-center">
          <Spin className="mt-10 mb-8"/>
        </Col>
      </Row>
    );
  }

  // Else
  return (
    <Fragment>
      <GraphContext.Provider value={{
        elements: getCurrentElements(),
        graphDirection: layout,
        onElementsUpdated: onElementsUpdated,
        deleteSelectedNodes: deleteSelectedNodes,
        cloneSelectedElements: cloneSelectedElements,
        openEditForm: openEditForm,
        closeEditForm: closeEditForm,
        openAddNewNodePanel: openAddNewNodePanel,
        closeAddNewNodePanel: closeAddNewNodePanel,
        addNewElements: addNewElements,
        addSelectedNodes: addSelectedNodes,
        unselectNodesAndEdges: unselectNodesAndEdges,
        disabledNodeTypes: disabledNodeTypes
      }}>
        <div className={isFullScreenMode ? `${baseClassName} fullscreen-flowgraph` : baseClassName} onClick={handleAddPanelCloseOnClick}>
          <Row className={isFullScreenMode ? 'mb-3 mt-3' : 'mb-3'} gutter={[16, 8]}>
            <Col
              flex="auto"
            >
              <Space size="small">

                {
                  canEdit ? (
                    <Row>
                      <Col>
                        <Dropdown
                          open={isAddNodePanelOpen}
                          overlay={<AddNodePanel ref={addNewNodePanelRef}/>}
                          disabled={!isEditingPrimaryLanguage}
                          destroyPopupOnHide
                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        >
                          <Button
                            type="text"
                            size="large"
                            onClick={() => setIsAddNodePanelOpen(!isAddNodePanelOpen)}
                            block={isMobileSize}
                            icon={<Fragment><PlusCircleOutlined /><ArrowDownIcon /></Fragment>}
                            className={classNames('mr-1', { 'pointer-events-none': !isEditingPrimaryLanguage })}
                          >
                            {t('Add new node')}
                          </Button>
                        </Dropdown>
                      </Col>
                    </Row>

                  ) : null
                }

                <Button
                  type="text"
                  size="large"
                  hidden={isViewOnlyMode}
                  onClick={() => dispatch(toggleFullScreenMode())}
                  icon={isFullScreenMode ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                >
                  {isFullScreenMode ? t('Exit fullscreen') : t('Enter fullscreen')}
                </Button>

                {
                  allowLayoutChange ? (
                    <Popover
                      placement="bottom"
                      content={
                        <Space direction="vertical">
                          <Button
                            type="text"
                            block
                            onClick={() => changeLayoutDirection(GRAPH_LAYOUTS.VERTICAL)}
                          >
                            {t('Vertical')}
                          </Button>

                          <Button
                            type="text"
                            block
                            onClick={() => changeLayoutDirection(GRAPH_LAYOUTS.HORIZONTAL)}
                          >
                            {t('Horizontal')}
                          </Button>
                        </Space>
                      }
                    >
                      <Button
                        type="text"
                        size="large"
                        hidden={isViewOnlyMode}
                        icon={<PartitionOutlined rotate={layout === GRAPH_LAYOUTS.VERTICAL ? 90 : 0} />}
                      >
                        {t('Change layout')}
                      </Button>
                    </Popover>
                  ) : null
                }
              </Space>
            </Col>

            {
              canEdit ? (
                <Col flex={isMobileSize ? 'auto' : '180px'}>
                  <Button
                    onClick={onSaveElements}
                    type="primary"
                    block
                    className="mb-2"
                    size="large"
                    disabled={!hasPendingChanges || isLoading}
                    loading={isLoading}
                  >
                    {t('Save Changes')}
                  </Button>
                </Col>
              ) : null
            }
          </Row>

          <div className={isFullScreenMode ? 'reactflow-wrapper-fullscreen' : 'reactflow-wrapper'} ref={reactFlowWrapper} onMouseMove={(event) => {
            if (reactFlowInstance) {
              mousePositionRef.current = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY
              });
            }
          }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={NODE_TYPES_COMPONENTS}
              zoomOnScroll={false}
              preventScrolling={false}
              zoomActivationKeyCode={['Alt']}
              deleteKeyCode={['Backspace', 'Delete']}

              onNodesChange={onNodesChanged}
              onEdgesChange={onEdgesChanged}
              onNodeDragStop={() => updateHistory(getCurrentElements())}
              onConnect={onEdgeConnect}
              onEdgeUpdate={canEdit && isEditingPrimaryLanguage ? onEdgeUpdate : undefined}
              onDoubleClick={onDoubleClick}
              onNodesDelete={onElementsRemove}
              onEdgesDelete={(edgesToDelete) => {
              // If the user presses delete, edges will be removed regardless because of onEdgesChange
              // This function is used to handle any cleanups and update history
              // We only need to update history if ONLY edges are removed, and that is because, if a node
              // is removed, adjacent edges are removed as well
              // TODO: Move to external function
                const selectedNodes = getNodes().filter(n => n.selected);
                if (is.emptyArray(selectedNodes)) {
                  onElementsRemove(edgesToDelete);
                }
              }}

              onInit={(reactFlowInstance) => setReactFlowInstance(reactFlowInstance)}
              onDrop={onNewNodeDrop}
              onDragOver={(event) => event.preventDefault()}
              onPaneClick={onPaneClick}
              elementsSelectable={canEdit}
              nodesDraggable={canEdit && isEditingPrimaryLanguage}
              nodesConnectable={canEdit && isEditingPrimaryLanguage}
              onlyRenderVisibleElements // For performance optimization in larger graphs
              snapToGrid={true}
              snapGrid={[10, 10]}
            >
              <MiniMap
                pannable
                zoomable
                nodeClassName={node => `react-flow__node__minimap react-flow__node-${node.type}__minimap`}
              />

              <Controls
                className="controls-lg"
                showInteractive={canEdit}
              >
                {
                  canEdit ? (
                    <Fragment>
                      {
                        isEditingPrimaryLanguage ? (
                          <Tooltip title={t('Undo')} placement="right" mouseEnterDelay={0.5}>
                            <ControlButton onClick={() => elementsHistory.back(1)}>
                              <UndoIcon />
                            </ControlButton>
                          </Tooltip>
                        ) : null
                      }

                      {
                        isEditingPrimaryLanguage ? (
                          <Tooltip title={t('Redo')} placement="right" mouseEnterDelay={0.5}>
                            <ControlButton onClick={() => elementsHistory.forward(1)}>
                              <RedoIcon />
                            </ControlButton>
                          </Tooltip>
                        ) : null
                      }

                      <ControlButton onClick={() => setIsExportModalOpen(true)}>
                        <Tooltip title={t('Export diagram')} placement="right" mouseEnterDelay={1}>
                          <DownloadOutlined />
                        </Tooltip>
                      </ControlButton>

                      {
                        is.dev() ? (
                          <Tooltip title={t('Log Flow Graph (DEV only)')} placement="right" mouseEnterDelay={0.5}>
                            <ControlButton
                              onClick={() => {
                                openConsoleAlertMessage({ type: 'success', message: t('Graph logged, check the console.') });
                              }}
                            >
                              <CodeIcon />
                            </ControlButton>
                          </Tooltip>
                        ) : null
                      }

                      {
                        graphSettingsForm ? (
                          <Tooltip title={t('Flow settings')} placement="right" mouseEnterDelay={0.5}>
                            <ControlButton onClick={() => setIsSettingsModalOpen(true)}>
                              <SettingOutlined />
                            </ControlButton>
                          </Tooltip>
                        ) : null
                      }

                      <ControlButton onClick={() => changeLayoutDirection(layout)}>
                        <Tooltip title={t('Align graph to layout')} placement="right" mouseEnterDelay={1}>
                          <BorderOuterOutlined />
                        </Tooltip>
                      </ControlButton>

                      <ControlButton onClick={() => handleStickyNoteIconClick()}>
                        <Tooltip title={t('Sticky note')} placement="right" mouseEnterDelay={1}>
                          <StickyNoteIcon />
                        </Tooltip>
                      </ControlButton>
                    </Fragment>
                  ) : (
                    <Fragment>
                      {
                        isViewOnlyMode ? (
                          <ControlButton onClick={() => setIsExportModalOpen(true)}>
                            <Tooltip title="Export diagram" placement="right" mouseEnterDelay={1}>
                              <DownloadOutlined />
                            </Tooltip>
                          </ControlButton>
                        ) : null
                      }
                    </Fragment>
                  )
                }
              </Controls>

              <Background variant="lines" gap={15} />
            </ReactFlow>
          </div>

          {
            canEdit && nodeToEdit ? (
              <SideModal
                title={renderNodeTitle(NODE_TYPES_PROPERTIES[nodeToEdit.type])}
                open={isEditFormOpen}
                destroyOnClose
                bodyClassName="p-0"
                onCancel={() => nodeEditFormRef.current?.onBeforeCancel(closeEditForm)}
                onClose={() => nodeEditFormRef.current?.onBeforeCancel(closeEditForm)}
              >
                <NodeEditForm
                  ref={nodeEditFormRef}
                  selectedElement={nodeToEdit}
                  metadata={nodeToEditMetadata}
                  bodyClassName="px-6 pt-6 flex-grow-1"
                  footerClassName="ant-drawer-footer"
                />
              </SideModal>
            ) : null
          }

          {
            graphSettingsForm && isSettingsModalOpen ? (
              <ReactFlowProvider>
                <Modal
                  title={t('Graph settings')}
                  open={isSettingsModalOpen}
                  onCancel={() => setIsSettingsModalOpen(false)}
                  style={{ maxWidth: 400 }}
                  width={'95%'}
                  centered
                  footer={null}
                  destroyOnClose
                >
                  {graphSettingsForm}
                </Modal>
              </ReactFlowProvider>
            ) : null
          }

          <Prompt
            when={hasPendingChanges}
            onConfirm={async (tx) => {
              // Tx is passed upwards to handle the navigation manually
              const isSaveSuccessful = await onSaveElements(tx.location);
              if (isSaveSuccessful) {
                tx.retry();
              }
            }}
            okText={t('Save Changes')}
            cancelText={t('Quit without saving')}
            title={t('Save Changes')}
            message={t('Your unsaved changes will be lost. Save changes before closing?')}
          />
        </div>
      </GraphContext.Provider>
      {
        /* GraphExportModal was seperated from the main flowGraph Context
           because they are considered independent of each other and also
           to have a different graph layout exported without affecting the main
           graph
         */
      }
      <GraphContext.Provider value={{
        elements: getCurrentElements(),
        graphDirection: GRAPH_LAYOUTS.VERTICAL
      }}>
        {
          // Not rendering when not needed for avoiding all the logic within the component.
          // Only downside is the missing animation on modal close.
          isExportModalOpen ? (
            <ReactFlowProvider>
              <GraphExportModal
                getLayoutedElements={getLayoutedElements}
                elements={getCurrentElements()}
                onClose={() => setIsExportModalOpen(false)}
                isModalOpen={isExportModalOpen}
                baseClassName={baseClassName}
              />
            </ReactFlowProvider>
          ) : null
        }
      </GraphContext.Provider>
    </Fragment>
  );
};

const FlowGraph = props => {
  const {
    graphId = 'flow-graph',
    isLoading = false,
    isViewOnlyMode = false,
    injectedNodeData = {},
    disabledNodeTypes = [],
    onUpdate = () => {},
    initialGraphDirection = GRAPH_LAYOUTS.VERTICAL,
    initialGraphElements = [],
    allowLayoutChange = true,
    graphSettingsForm = null,
    hasPendingChanges = false,
    setHasPendingChanges = () => {}
  } = props;
  const dispatch = useDispatch();

  // On mount
  useEffect(() => {
    // Make sure the full screen mode is disabled when the component unmounts
    return () => {
      dispatch(toggleFullScreenMode(false));
    };
  }, []);

  return (
    <ReactFlowProvider>
      <WrappedFlowGraph
        graphId={graphId}
        initialGraphElements={initialGraphElements}
        isLoading={isLoading}
        injectedNodeData={injectedNodeData}
        isViewOnlyMode={isViewOnlyMode}
        disabledNodeTypes={disabledNodeTypes}
        onUpdate={onUpdate}
        hasPendingChanges={hasPendingChanges}
        setHasPendingChanges={setHasPendingChanges}
        initialGraphDirection={initialGraphDirection}
        allowLayoutChange={allowLayoutChange}
        graphSettingsForm={graphSettingsForm}
      />
    </ReactFlowProvider>
  );
};

WrappedFlowGraph.propTypes = {
  graphId: PropTypes.string,
  initialGraphElements: PropTypes.array,
  isLoading: PropTypes.bool,
  isViewOnlyMode: PropTypes.bool,
  injectedNodeData: PropTypes.object,
  disabledNodeTypes: PropTypes.array,
  onUpdate: PropTypes.func,
  initialGraphDirection: PropTypes.string,
  allowLayoutChange: PropTypes.bool,
  graphSettingsForm: PropTypes.node,
  hasPendingChanges: PropTypes.bool,
  setHasPendingChanges: PropTypes.func
};

FlowGraph.propTypes = {
  graphId: PropTypes.string,
  initialGraphElements: PropTypes.array,
  isLoading: PropTypes.bool,
  isViewOnlyMode: PropTypes.bool,
  injectedNodeData: PropTypes.object,
  disabledNodeTypes: PropTypes.array,
  onUpdate: PropTypes.func,
  initialGraphDirection: PropTypes.string,
  allowLayoutChange: PropTypes.bool,
  graphSettingsForm: PropTypes.node,
  hasPendingChanges: PropTypes.bool,
  setHasPendingChanges: PropTypes.func
};

export default FlowGraph;
